/**
 * Vuex Theme Store Module (Vue 2)
 * Tema yönetimi için state, mutations, actions ve getters
 */

import {
  THEMES,
  DEFAULT_THEME,
  applyTheme,
  loadSavedTheme,
  getCurrentTheme,
  getThemeInfo,
  getAvailableThemes,
} from "@/plugins/theme";

const state = {
  // Mevcut aktif tema
  currentTheme: loadSavedTheme(),

  // Tüm mevcut temalar
  availableThemes: getAvailableThemes(),

  // Tema geçiş animasyonu durumu
  isTransitioning: false,

  // Dark mode durumu (opsiyonel)
  isDarkMode: false,
};

const getters = {
  /**
   * Mevcut tema adını döndürür
   */
  currentTheme: (state) => state.currentTheme,

  /**
   * Mevcut tema bilgilerini döndürür
   */
  currentThemeInfo: (state) => getThemeInfo(state.currentTheme),

  /**
   * Ana tema rengini döndürür
   */
  primaryColor: (state, getters) => getters.currentThemeInfo.color,

  /**
   * Tüm mevcut temaları döndürür
   */
  availableThemes: (state) => state.availableThemes,

  /**
   * Tema geçiş durumunu döndürür
   */
  isTransitioning: (state) => state.isTransitioning,

  /**
   * Dark mode durumunu döndürür
   */
  isDarkMode: (state) => state.isDarkMode,

  /**
   * Belirli bir temanın aktif olup olmadığını kontrol eder
   */
  isThemeActive: (state) => (themeName) => state.currentTheme === themeName,

  /**
   * Vuetify için tema konfigürasyonu
   */
  vuetifyTheme: (state, getters) => getters.currentThemeInfo.vuetifyTheme,
};

const mutations = {
  /**
   * Mevcut temayı günceller
   */
  SET_THEME(state, themeName) {
    if (THEMES[themeName]) {
      state.currentTheme = themeName;
    } else {
      console.warn(`Geçersiz tema: ${themeName}`);
      state.currentTheme = DEFAULT_THEME;
    }
  },

  /**
   * Tema geçiş durumunu günceller
   */
  SET_TRANSITIONING(state, isTransitioning) {
    state.isTransitioning = isTransitioning;
  },

  /**
   * Mevcut temaları günceller (dynamic theme loading için)
   */
  SET_AVAILABLE_THEMES(state, themes) {
    state.availableThemes = themes;
  },

  /**
   * Dark mode durumunu günceller
   */
  SET_DARK_MODE(state, isDark) {
    state.isDarkMode = isDark;
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  },
};

const actions = {
  /**
   * Tema değiştirme işlemi
   * @param {object} context - Vuex context
   * @param {string} themeName - Yeni tema adı
   */
  async changeTheme({ commit, state, rootState }, { themeName, vuetify }) {
    // Aynı tema ise işlem yapma
    if (state.currentTheme === themeName) {
      return state.currentTheme;
    }

    // Geçiş animasyonunu başlat
    commit("SET_TRANSITIONING", true);

    try {
      // Temayı uygula
      const appliedTheme = applyTheme(themeName, vuetify);

      // State'i güncelle
      commit("SET_THEME", appliedTheme);

      // Kısa bir gecikme (smooth transition için)
      await new Promise((resolve) => setTimeout(resolve, 300));

      return appliedTheme;
    } catch (error) {
      console.error("Tema değiştirme hatası:", error);
      throw error;
    } finally {
      // Geçiş animasyonunu sonlandır
      commit("SET_TRANSITIONING", false);
    }
  },

  /**
   * Kaydedilmiş temayı yükler
   */
  loadSavedTheme({ commit }, vuetify) {
    const savedTheme = loadSavedTheme();
    commit("SET_THEME", savedTheme);
    applyTheme(savedTheme, vuetify);
    return savedTheme;
  },

  /**
   * Default temaya geri döner
   */
  resetToDefaultTheme({ dispatch }, vuetify) {
    return dispatch("changeTheme", { themeName: DEFAULT_THEME, vuetify });
  },

  /**
   * Tema listesini günceller (dynamic loading için)
   */
  updateAvailableThemes({ commit }) {
    const themes = getAvailableThemes();
    commit("SET_AVAILABLE_THEMES", themes);
  },

  /**
   * Dark mode toggle
   */
  toggleDarkMode({ commit, state }) {
    commit("SET_DARK_MODE", !state.isDarkMode);
    return state.isDarkMode;
  },

  /**
   * Dark mode set
   */
  setDarkMode({ commit }, isDark) {
    commit("SET_DARK_MODE", isDark);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
